import { useEffect, useRef, useState } from "react";

import { clearCelebratedToday, hasCelebratedToday, markCelebratedToday } from "./celebration";

interface CelebrationState {
  showConfetti: boolean;
  showCelebration: boolean;
  dismissCelebration: () => void;
  /** 動作確認用。「見た」記録を消してから、その場で演出を出し直す(→ ScreenHeader のメニュー) */
  replayCelebration: () => void;
}

/**
 * 記念日当日、その日はじめて画面を開いたときだけモーダルと紙吹雪を出す。
 * 「はじめて」は localStorage(→ lib/schedule/celebration.ts)でユーザーごと・
 * 日付ごとに記録し、ref はカテゴリ絞り込みなどの再レンダーで同じ回の効果が
 * 二重に発火しないようにするためだけに使う。
 */
export function useAnniversaryCelebration(
  isAnniversaryToday: boolean,
  isMilestoneToday: boolean,
  selfId: string | undefined,
  todayKey: string,
): CelebrationState {
  const celebrationFired = useRef(false);
  const confettiTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  function playCelebration() {
    setShowCelebration(true);
    clearTimeout(confettiTimer.current);
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShowConfetti(false);
      return;
    }
    setShowConfetti(true);
    confettiTimer.current = setTimeout(
      () => {
        setShowConfetti(false);
      },
      isMilestoneToday ? 16000 : 11200,
    );
  }

  useEffect(() => {
    if (celebrationFired.current || !isAnniversaryToday || selfId === undefined) return;
    celebrationFired.current = true;
    if (hasCelebratedToday(selfId, todayKey)) return;
    markCelebratedToday(selfId, todayKey);
    playCelebration();
  }, [isAnniversaryToday, selfId, todayKey]);

  return {
    showConfetti,
    showCelebration,
    dismissCelebration: () => {
      setShowCelebration(false);
    },
    replayCelebration: () => {
      if (selfId === undefined) return;
      clearCelebratedToday(selfId, todayKey);
      playCelebration();
    },
  };
}
